import { Link, useParams } from "react-router";

import { RegistryFailure, RegistryLoading } from "@/components/RegistryLookupState";
import { RegistryRecordCard } from "@/components/RegistryRecordCard";
import { Card, PageHeader } from "@/components/ui";
import { NETWORK_NAME } from "@/lib/config";
import { usePageMeta } from "@/lib/page-meta";
import { resolveRegistryId } from "@/lib/registry/ids";
import { useRegistryLookup } from "@/lib/registry/use-lookup";

import { NotFoundPage } from "./NotFoundPage";

const CONTEXT = "Registry verification";

export function VerifyPage() {
  const { registryId: raw = "" } = useParams();

  usePageMeta({
    title: `Verifikasi ${decodeURIComponent(raw)}`,
    description:
      "Periksa status resmi sebuah utility, vendor, atau layanan vendor terhadap ASETQU UtilityRegistry di rantai.",
  });

  // A malformed id is the only genuine 404 here: there is nothing to look up.
  // An id that simply is not registered is a real answer, and gets rendered.
  const resolved = resolveRegistryId(raw);
  const lookup = useRegistryLookup(resolved ?? undefined);

  if (!resolved) return <NotFoundPage />;
  if (lookup.status === "loading") return <RegistryLoading context={CONTEXT} />;
  if (!lookup.lookup.ok) {
    return <RegistryFailure context={CONTEXT} failure={lookup.lookup.failure} />;
  }

  const record = lookup.lookup.record;
  const { versions, children } = record;

  return (
    <div className="page-shell">
      <PageHeader
        context={CONTEXT}
        title={record.name ?? "CATATAN REGISTRY"}
        description={`Status efektif dibaca langsung dari UtilityRegistry di ${NETWORK_NAME}.`}
      />

      <RegistryRecordCard record={record} />

      {versions.length > 1 && (
        <Card title="Riwayat versi">
          <p className="mb-3 text-sm text-ink-muted">
            Versi lama tetap ditampilkan. Versi <strong>Deprecated</strong> tidak menerima
            transaksi baru tetapi harus tetap dapat menyelesaikan posisi yang sudah ada.
          </p>
          <ul className="flex flex-wrap gap-2">
            {versions.map((version) => (
              <li key={version}>
                <Link
                  className="inline-flex rounded-lg px-3 py-1 text-sm text-ink ring-1 ring-inset ring-line hover:text-brand hover:ring-brand"
                  to={`/utility/${raw}?version=${version}`}
                >
                  v{version}
                  {version === record.version ? " (ditampilkan)" : ""}
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {children.length > 0 && (
        <Card title="Layanan vendor ini">
          <p className="mb-3 text-sm text-ink-muted">
            Kalau vendor ditangguhkan atau dicabut, seluruh layanan di bawah ini berhenti menerima
            aktivitas baru secara otomatis.
          </p>
          <ul className="flex flex-col gap-2">
            {children.map((child) => (
              <li key={child}>
                <Link
                  className="font-mono text-sm text-brand underline decoration-dotted underline-offset-4 hover:decoration-solid"
                  to={`/utility/${child}`}
                >
                  {child}
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
